// src/Payroll_Management/PayRoll_Report/SalaryCommitmentReportPrint.jsx
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Printer, X } from "lucide-react";

const BASE_URL = "https://nissi-india.com"; // change if needed

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

function s(v) {
  return (v ?? "").toString().trim();
}
function toNum(v, def = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
}
function money(v) {
  const n = toNum(v, 0);
  return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function parseAmt(v) {
  if (v === null || v === undefined) return 0;
  const n = Number(String(v).replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
}

// ASP prevdetails() equivalent
function getPrevMonthYear(monthName, year) {
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];
  const idx = months.findIndex((m) => m.toLowerCase() === String(monthName).toLowerCase());
  if (idx < 0) return { prevMonth: "", prevYear: year };
  if (idx === 0) return { prevMonth: "December", prevYear: year - 1 };
  return { prevMonth: months[idx - 1], prevYear: year };
}

export default function SalaryCommitmentReportPrint() {
  const q = useQuery();

  const finYearId = toNum(localStorage.getItem("finYearId") || q.get("finYearId"), 0);

  const branchId = s(q.get("branchId") || q.get("branch") || "");
  const monthName = s(q.get("monthName") || q.get("month") || "");
  const year = toNum(q.get("year"), new Date().getFullYear());

  const { prevMonth, prevYear } = useMemo(
    () => getPrevMonthYear(monthName, year),
    [monthName, year]
  );

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  const branchName = s(q.get("branchName") || "");

  const fetchPrint = async () => {
    if (!finYearId) return toast.warn("finYearId missing");
    if (!monthName || monthName === "Select") return toast.warn("Month is required");
    if (!branchId || branchId === "Select") return toast.warn("Branch is required");

    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/api/salary-commitment-report/print-data`, {
        params: {
          finYearId,
          branchId,
          monthName,
          year,
          prevMonth,
          prevYear,
        },
      });

      if (res?.data?.success) setRows(res.data.rows || []);
      else toast.error(res?.data?.message || "Failed to load print data");
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrint();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totals = useMemo(() => {
    const t = {
      Working: 0,
      Leave: 0,
      Totalnet: 0,
      HRA: 0,
      Conveyance: 0,
      TTWAllowance: 0,
      Totalallowance: 0,
      Total: 0,
      PreVious: 0,
      Bata: 0,
    };

    for (const r of rows) {
      t.Working += parseAmt(r.Working);
      t.Leave += parseAmt(r.Leave);
      t.Totalnet += parseAmt(r.Totalnet);
      t.HRA += parseAmt(r.HRA);
      t.Conveyance += parseAmt(r.Conveyance);
      t.TTWAllowance += parseAmt(r.TTWAllowance);
      t.Totalallowance += parseAmt(r.Totalallowance);
      t.Total += parseAmt(r.Total);
      t.PreVious += parseAmt(r.PreVious);
      t.Bata += parseAmt(r.Bata);
    }

    const honorMinus = rows.length ? parseAmt(rows[0]?.fld_honorariumminus) : 0;
    const honorAge60 = rows.length ? parseAmt(rows[0]?.fld_honorariumage60) : 0;

    return {
      ...t,
      honorMinus,
      honorAge60,
      grandTotal: t.Total + honorMinus + honorAge60,
    };
  }, [rows]);

  const pageTitle = `Salary Commitment for the month of ${monthName}-${year}`;

  const handlePrint = () => window.print();
  const handleClose = () => window.close();

  return (
    <div className="min-h-screen bg-gray-100 p-4 print:bg-white print:p-0">
      <ToastContainer position="top-right" autoClose={2500} />

      {/* controls (hidden on print) */}
      <div className="mx-auto max-w-5xl print:hidden flex items-center justify-end gap-3 mb-4">
        <button
          onClick={handlePrint}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-600 text-white font-bold hover:bg-emerald-700 shadow"
        >
          <Printer className="w-4 h-4" /> Print
        </button>
        <button
          onClick={handleClose}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-700 text-white font-bold hover:bg-gray-800 shadow"
        >
          <X className="w-4 h-4" /> Close
        </button>
      </div>

      {/* print sheet */}
      <div className="mx-auto max-w-5xl bg-white shadow p-6 print:shadow-none print:p-0">
        <style>{`
          /* Screen: allow scroll if needed */
          .table-wrapper { overflow-x: auto; }

          /* Print: absolutely NO scrollbars anywhere */
          @media print {
            @page { size: A4 landscape; margin: 10mm; } /* landscape helps wide table */

            html, body { overflow: visible !important; height: auto !important; }
            .table-wrapper { overflow: visible !important; }
            .print-no-overflow { overflow: visible !important; }

            table { width: 100% !important; table-layout: fixed !important; }
            th, td { word-break: break-word; overflow: visible !important; }

            /* Fit table in one page better */
            .print-fit { font-size: 10px !important; }
            .print-fit th, .print-fit td { padding: 4px !important; }

            /* Remove background colors if any */
            * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        `}</style>

        <div className="border border-black print-no-overflow">
          {/* Header */}
          <div className="p-4">
            <div className="flex items-center justify-between">
              <div className="w-28 h-20 border border-black flex items-center justify-center text-xs">
                LOGO
              </div>

              <div className="text-center flex-1 px-4">
                <div className="text-xl font-extrabold">Nissi Infotech</div>
                <div className="text-sm font-semibold">Nissi</div>
                <div className="text-sm font-extrabold">CIVIL CONSTRUCTION</div>
                <div className="text-xs mt-1">
                  3/42-12, Nagappa colony, kavundampalayam, Coimbatore - 641030, Tamil Nadu, India
                </div>
                <div className="text-xs">Phone No : 9003744644</div>
                <div className="text-xs">infraconsot-implementation@nissii</div>
              </div>

              <div className="w-28" />
            </div>
          </div>

          <div className="border-t border-black text-center py-2 font-bold">
            {pageTitle}
          </div>

          {/* Table */}
          <div className="p-3">
            {/* ✅ IMPORTANT: use table-wrapper class (NOT overflow-x-auto) */}
            <div className="table-wrapper">
              <table className="w-full text-xs border border-black print-fit">
                <thead>
                  <tr className="border-b border-black">
                    <th className="border-r border-black p-2 text-center w-10">S.No</th>
                    <th className="border-r border-black p-2 text-center w-28">Location</th>
                    <th className="border-r border-black p-2 text-center">Net Working Rs</th>
                    <th className="border-r border-black p-2 text-center">Net Leave Rs</th>
                    <th className="border-r border-black p-2 text-center">Net Total Rs</th>
                    <th className="border-r border-black p-2 text-center">Spl HRA</th>
                    <th className="border-r border-black p-2 text-center">Conveyance</th>
                    <th className="border-r border-black p-2 text-center">TTWAllowance</th>
                    <th className="border-r border-black p-2 text-center">Total Allowance</th>
                    <th className="border-r border-black p-2 text-center">NetSalary+Allowance</th>
                    <th className="border-r border-black p-2 text-center">Previous Month Salary</th>
                    <th className="p-2 text-center">{`Bata (${monthName}-${year})`}</th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={12} className="p-6 text-center">Loading...</td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={12} className="p-6 text-center">No Records Found</td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => (
                      <tr key={idx} className="border-t border-black">
                        <td className="border-r border-black p-2 text-center">{idx + 1}</td>
                        <td className="border-r border-black p-2 text-left">
                          {r.fld_branchname || r.Location || branchName}
                        </td>
                        <td className="border-r border-black p-2 text-right">{money(r.Working)}</td>
                        <td className="border-r border-black p-2 text-right">{money(r.Leave)}</td>
                        <td className="border-r border-black p-2 text-right">{money(r.Totalnet)}</td>
                        <td className="border-r border-black p-2 text-right">{money(r.HRA)}</td>
                        <td className="border-r border-black p-2 text-right">{money(r.Conveyance)}</td>
                        <td className="border-r border-black p-2 text-right">{money(r.TTWAllowance)}</td>
                        <td className="border-r border-black p-2 text-right">{money(r.Totalallowance)}</td>
                        <td className="border-r border-black p-2 text-right">{money(r.Total)}</td>
                        <td className="border-r border-black p-2 text-right">{money(r.PreVious)}</td>
                        <td className="p-2 text-right">{money(r.Bata)}</td>
                      </tr>
                    ))
                  )}

                  {rows.length > 0 && (
                    <tr className="border-t border-black font-bold">
                      <td className="border-r border-black p-2 text-center" colSpan={2}>
                        Total Amount in Rupees
                      </td>
                      <td className="border-r border-black p-2 text-right">{money(totals.Working)}</td>
                      <td className="border-r border-black p-2 text-right">{money(totals.Leave)}</td>
                      <td className="border-r border-black p-2 text-right">{money(totals.Totalnet)}</td>
                      <td className="border-r border-black p-2 text-right">{money(totals.HRA)}</td>
                      <td className="border-r border-black p-2 text-right">{money(totals.Conveyance)}</td>
                      <td className="border-r border-black p-2 text-right">{money(totals.TTWAllowance)}</td>
                      <td className="border-r border-black p-2 text-right">{money(totals.Totalallowance)}</td>
                      <td className="border-r border-black p-2 text-right">{money(totals.Total)}</td>
                      <td className="border-r border-black p-2 text-right">{money(totals.PreVious)}</td>
                      <td className="p-2 text-right">{money(totals.Bata)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Summary blocks */}
            {rows.length > 0 && (
              <div className="mt-4 text-sm">
                <div className="grid grid-cols-1 gap-2 w-full max-w-xl">
                  <div className="flex justify-between border border-black px-3 py-2">
                    <div className="font-bold">Salary + Allowance (Rs.)</div>
                    <div className="font-bold">{money(totals.Total)}</div>
                  </div>
                  <div className="flex justify-between border border-black px-3 py-2">
                    <div className="font-bold">Honorarium (Mines) (Rs.)</div>
                    <div className="font-bold">{money(totals.honorMinus)}</div>
                  </div>
                  <div className="flex justify-between border border-black px-3 py-2">
                    <div className="font-bold">Honorarium (Age 60) (Rs.)</div>
                    <div className="font-bold">{money(totals.honorAge60)}</div>
                  </div>
                  <div className="flex justify-between border border-black px-3 py-2">
                    <div className="font-bold">Total (Rs.)</div>
                    <div className="font-bold">{money(totals.grandTotal)}</div>
                  </div>
                </div>

                {/* Signatures */}
                <div className="mt-10 grid grid-cols-5 gap-6 text-center font-bold">
                  <div>Prepared By</div>
                  <div>Checked By</div>
                  <div>Checked By</div>
                  <div>GM Finance</div>
                  <div>Approved By</div>
                </div>

                {/* Footer */}
                <div className="mt-6 border border-black p-2 text-center text-xs">
                  Report generated by ERP-Software - www.infraconsoft.com owned by www.nissiinfotech.com
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
